import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { serviceDetails, pricingPlans } from '../data/mock';
import { Button } from '../components/ui/button';

export default function ServicesPage() {
  const pricingRef = useRef(null);
  const pricingInView = useInView(pricingRef, { once: true, margin: '-80px' });

  return (
    <main>
      {/* Page Hero */}
      <section className="relative pt-36 pb-20">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[400px] bg-blue-600/6 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">
              Services
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] text-[var(--text-primary)] mb-6 leading-tight">
              Digital Solutions That
              <br />
              <span className="text-blue-400">Drive Growth</span>
            </h1>

            <p className="text-lg text-[var(--text-muted)] max-w-2xl leading-relaxed">
              From web development to digital marketing, we offer comprehensive services designed to help your business succeed online.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Details */}
      {serviceDetails.map((service, sIndex) => {
        const isEven = sIndex % 2 === 0;
        return (
          <ServiceDetailSection
            key={service.id}
            service={service}
            isEven={isEven}
          />
        );
      })}

      {/* Pricing */}
      <section ref={pricingRef} className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={pricingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">
              Pricing
            </span>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Syne'] text-[var(--text-primary)] mb-5">
              Choose Your Plan
            </h2>

            <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto">
              Flexible packages tailored to your needs. Every plan includes dedicated support and regular reporting.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 40 }}
                animate={pricingInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className={`relative p-8 rounded-2xl border transition-all duration-500 ${plan.highlighted
                    ? 'bg-[var(--card-color)] border-blue-500/40 shadow-lg shadow-blue-500/5'
                    : 'bg-[var(--card-color)] border-[var(--border-color)] hover:border-slate-700/60'
                  }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold uppercase tracking-wider">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold font-['Syne'] text-[var(--text-primary)] mb-2">
                    {plan.name}
                  </h3>

                  <p className="text-[var(--text-muted)] text-sm mb-4">
                    {plan.description}
                  </p>

                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold font-['Syne'] text-[var(--text-primary)]">
                      ${plan.price}
                    </span>
                    <span className="text-[var(--text-muted)] text-sm">
                      {plan.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                      <span className="text-[var(--text-secondary)] text-sm">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact">
                  <Button
                    className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${plan.highlighted
                        ? 'bg-blue-600 hover:bg-blue-500 text-white hover:shadow-lg hover:shadow-blue-600/20'
                        : 'bg-[var(--surface-2)] hover:bg-slate-700/80 text-[var(--text-primary)]'
                      }`}
                  >
                    Get Started
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function ServiceDetailSection({ service, isEven }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section
      ref={ref}
      className={`relative py-20 ${isEven ? '' : 'bg-[var(--bg-soft)]'}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center">
              <service.icon className="w-6 h-6 text-blue-400" />
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-['Syne'] text-[var(--text-primary)]">
                {service.title}
              </h2>
              <p className="text-blue-400 text-sm font-medium">
                {service.tagline}
              </p>
            </div>
          </div>

          <p className="text-[var(--text-muted)] text-lg leading-relaxed max-w-3xl mt-4">
            {service.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {service.highlights.map((item, i) => {
            const HIcon = item.icon;
            return (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                className="p-7 rounded-2xl bg-[var(--card-color)] border border-[var(--border-color)] hover:border-blue-500/20 transition-all duration-500 group"
              >
                <div className="w-11 h-11 rounded-lg bg-blue-600/10 flex items-center justify-center mb-5 group-hover:bg-blue-600/15 transition-colors duration-500">
                  <HIcon className="w-5 h-5 text-blue-400" />
                </div>

                <h3 className="text-lg font-bold font-['Syne'] text-[var(--text-primary)] mb-2">
                  {item.label}
                </h3>

                <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}